import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTourismData } from '../../context/TourismContext';
import { getImageUrl, formatPrice, formatRating } from '../../services/api';

function DemoHomepage() {
    const { 
        featuredContent, 
        dashboardStats,
        loading, 
        fetchFeaturedDestinations,
        fetchFeaturedTours,
        fetchFeaturedActivities,
        fetchFeaturedResorts,
        fetchFeaturedBlogPosts,
        fetchDashboardStats
    } = useTourismData();

    const [activeSection, setActiveSection] = useState('destinations');

    useEffect(() => {
        fetchFeaturedDestinations();
        fetchFeaturedTours();
        fetchFeaturedActivities();
        fetchFeaturedResorts();
        fetchFeaturedBlogPosts();
        fetchDashboardStats();
    }, [fetchFeaturedDestinations, fetchFeaturedTours, fetchFeaturedActivities, fetchFeaturedResorts, fetchFeaturedBlogPosts, fetchDashboardStats]);

    return (
        <div className="demo-homepage">
            {/* Hero Section */}
            <section className="hero-section bg-primary text-white py-5">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            <h1 className="display-4 fw-bold mb-3">
                                🎉 Backend Integration Complete!
                            </h1>
                            <p className="lead mb-4">
                                Your React tourism template is now fully connected to the Django backend. 
                                All data below is loaded from the PostgreSQL database via the REST API.
                            </p>
                            <div className="d-flex gap-3">
                                <a href="https://tours.dzsoftech.net/admin/" className="btn btn-light btn-lg" target="_blank" rel="noopener noreferrer">
                                    Access Admin Panel
                                </a>
                                <Link to="/destination" className="btn btn-outline-light btn-lg">
                                    View All Destinations
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            {dashboardStats && (
                                <div className="stats-card bg-white text-dark p-4 rounded">
                                    <h5 className="mb-3">📊 Live Statistics</h5>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <div className="stat-item text-center">
                                                <h4 className="text-primary mb-0">{dashboardStats.total_destinations}</h4>
                                                <small>Destinations</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="stat-item text-center">
                                                <h4 className="text-success mb-0">{dashboardStats.total_tours}</h4>
                                                <small>Tours</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="stat-item text-center">
                                                <h4 className="text-warning mb-0">{dashboardStats.total_activities}</h4>
                                                <small>Activities</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="stat-item text-center">
                                                <h4 className="text-info mb-0">{dashboardStats.total_resorts}</h4>
                                                <small>Resorts</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Tabs */}
            <section className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="text-center mb-4">🔗 Live Backend Data</h2>
                            <p className="text-center text-muted mb-5">
                                The content below is fetched in real-time from your Django backend. 
                                Try adding new content through the admin panel to see it appear here!
                            </p>
                            
                            {/* Section Navigation */}
                            <ul className="nav nav-pills justify-content-center mb-4" role="tablist">
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeSection === 'destinations' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('destinations')}
                                    >
                                        🏞️ Destinations ({featuredContent.destinations?.length || 0})
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeSection === 'tours' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('tours')}
                                    >
                                        🎯 Tours ({featuredContent.tours?.length || 0})
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeSection === 'activities' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('activities')}
                                    >
                                        🏃 Activities ({featuredContent.activities?.length || 0})
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeSection === 'resorts' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('resorts')}
                                    >
                                        🏨 Resorts ({featuredContent.resorts?.length || 0})
                                    </button>
                                </li>
                            </ul>

                            {/* Content Display */}
                            <div className="tab-content">
                                {/* Destinations */}
                                {activeSection === 'destinations' && (
                                    <div className="row">
                                        {loading.destinations ? (
                                            <div className="col-12 text-center">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : featuredContent.destinations?.length > 0 ? (
                                            featuredContent.destinations.map((destination) => (
                                                <div key={destination.id} className="col-md-6 col-lg-4 mb-4">
                                                    <div className="card h-100 shadow-sm">
                                                        <img 
                                                            src={getImageUrl(destination.main_image) || '/assets/img/tour/tour_box_1.jpg'}
                                                            className="card-img-top" 
                                                            alt={destination.name}
                                                            style={{ height: '200px', objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.src = '/assets/img/tour/tour_box_1.jpg';
                                                            }}
                                                        />
                                                        {destination.featured && (
                                                            <span className="badge bg-primary position-absolute top-0 end-0 m-2">
                                                                Featured
                                                            </span>
                                                        )}
                                                        <div className="card-body">
                                                            <h5 className="card-title">{destination.name}</h5>
                                                            <p className="card-text text-muted small">
                                                                📍 {destination.city}, {destination.country}
                                                            </p>
                                                            <p className="card-text">{destination.short_description}</p>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className="h5 text-primary">{formatPrice(destination.price)}</span>
                                                                    <small className="text-muted">/person</small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="text-warning">
                                                                        ⭐ {formatRating(destination.rating)}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {destination.review_count} reviews
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3">
                                                                <span className={`badge bg-${destination.difficulty === 'easy' ? 'success' : destination.difficulty === 'moderate' ? 'warning' : 'danger'} me-2`}>
                                                                    {destination.difficulty}
                                                                </span>
                                                                <span className="badge bg-secondary">
                                                                    {destination.duration_days} days
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-transparent">
                                                            <Link 
                                                                to={`/destination/${destination.slug}`} 
                                                                className="btn btn-primary w-100"
                                                            >
                                                                View Details
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center">
                                                <div className="alert alert-info">
                                                    <h5>No featured destinations yet</h5>
                                                    <p>Add some destinations through the admin panel and mark them as featured!</p>
                                                    <a href="https://tours.dzsoftech.net/admin/tourism/destination/" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                                                        Add Destinations in Admin
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tours */}
                                {activeSection === 'tours' && (
                                    <div className="row">
                                        {loading.tours ? (
                                            <div className="col-12 text-center">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : featuredContent.tours?.length > 0 ? (
                                            featuredContent.tours.map((tour) => (
                                                <div key={tour.id} className="col-md-6 col-lg-4 mb-4">
                                                    <div className="card h-100 shadow-sm">
                                                        <img 
                                                            src={getImageUrl(tour.main_image) || '/assets/img/tour/tour_box_1.jpg'}
                                                            className="card-img-top" 
                                                            alt={tour.name}
                                                            style={{ height: '200px', objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.src = '/assets/img/tour/tour_box_1.jpg';
                                                            }}
                                                        />
                                                        {tour.featured && (
                                                            <span className="badge bg-primary position-absolute top-0 end-0 m-2">
                                                                Featured
                                                            </span>
                                                        )}
                                                        <div className="card-body">
                                                            <h5 className="card-title">{tour.name}</h5>
                                                            <p className="card-text">{tour.short_description}</p>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className="h5 text-primary">{formatPrice(tour.price)}</span>
                                                                    <small className="text-muted">/person</small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="text-warning">
                                                                        ⭐ {formatRating(tour.rating)}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {tour.review_count} reviews
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3">
                                                                <span className="badge bg-info me-2">
                                                                    {tour.tour_type}
                                                                </span>
                                                                <span className="badge bg-secondary">
                                                                    {tour.duration_days}D/{tour.duration_nights}N
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-transparent">
                                                            <Link 
                                                                to={`/tour-details`} 
                                                                className="btn btn-primary w-100"
                                                            >
                                                                View Tour Details
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center">
                                                <div className="alert alert-info">
                                                    <h5>No featured tours yet</h5>
                                                    <p>Add some tours through the admin panel and mark them as featured!</p>
                                                    <a href="https://tours.dzsoftech.net/admin/tourism/tour/" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                                                        Add Tours in Admin
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Activities */}
                                {activeSection === 'activities' && (
                                    <div className="row">
                                        {loading.activities ? (
                                            <div className="col-12 text-center">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : featuredContent.activities?.length > 0 ? (
                                            featuredContent.activities.map((activity) => (
                                                <div key={activity.id} className="col-md-6 col-lg-4 mb-4">
                                                    <div className="card h-100 shadow-sm">
                                                        <img 
                                                            src={getImageUrl(activity.main_image) || '/assets/img/tour/tour_box_1.jpg'}
                                                            className="card-img-top" 
                                                            alt={activity.name}
                                                            style={{ height: '200px', objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.src = '/assets/img/tour/tour_box_1.jpg';
                                                            }}
                                                        />
                                                        <div className="card-body">
                                                            <h5 className="card-title">{activity.name}</h5>
                                                            <p className="card-text">{activity.short_description}</p>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className="h5 text-primary">{formatPrice(activity.price)}</span>
                                                                    <small className="text-muted">/person</small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="text-warning">
                                                                        ⭐ {formatRating(activity.rating)}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {activity.review_count} reviews
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3">
                                                                <span className="badge bg-success me-2">
                                                                    {activity.activity_type}
                                                                </span>
                                                                <span className="badge bg-secondary">
                                                                    {activity.duration_hours}h
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-transparent">
                                                            <Link 
                                                                to={`/activities-details`} 
                                                                className="btn btn-primary w-100"
                                                            >
                                                                View Activity
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center">
                                                <div className="alert alert-info">
                                                    <h5>No featured activities yet</h5>
                                                    <p>Add some activities through the admin panel!</p>
                                                    <a href="https://tours.dzsoftech.net/admin/tourism/activity/" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                                                        Add Activities in Admin
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Resorts */}
                                {activeSection === 'resorts' && (
                                    <div className="row">
                                        {loading.resorts ? (
                                            <div className="col-12 text-center">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : featuredContent.resorts?.length > 0 ? (
                                            featuredContent.resorts.map((resort) => (
                                                <div key={resort.id} className="col-md-6 col-lg-4 mb-4">
                                                    <div className="card h-100 shadow-sm">
                                                        <img 
                                                            src={getImageUrl(resort.main_image) || '/assets/img/tour/tour_box_1.jpg'}
                                                            className="card-img-top" 
                                                            alt={resort.name}
                                                            style={{ height: '200px', objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.src = '/assets/img/tour/tour_box_1.jpg';
                                                            }}
                                                        />
                                                        <div className="card-body">
                                                            <h5 className="card-title">{resort.name}</h5>
                                                            <p className="card-text text-muted small">
                                                                📍 {resort.destination?.city}, {resort.destination?.country}
                                                            </p>
                                                            <p className="card-text">{resort.short_description}</p>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className="h5 text-primary">{formatPrice(resort.price_per_night)}</span>
                                                                    <small className="text-muted">/night</small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="text-warning">
                                                                        {'⭐'.repeat(resort.star_rating)}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {resort.star_rating} Star Hotel
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-transparent">
                                                            <Link 
                                                                to={`/resort/${resort.slug}`} 
                                                                className="btn btn-primary w-100"
                                                            >
                                                                View Resort
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center">
                                                <div className="alert alert-info">
                                                    <h5>No featured resorts yet</h5>
                                                    <p>Add some resorts through the admin panel!</p>
                                                    <a href="https://tours.dzsoftech.net/admin/tourism/resort/" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                                                        Add Resorts in Admin
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* API Information */}
            <section className="bg-light py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <h3 className="text-center mb-4">🔧 How It Works</h3>
                            <div className="row">
                                <div className="col-md-4 text-center mb-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body">
                                            <div className="display-4 text-primary mb-3">⚙️</div>
                                            <h5>Django Admin</h5>
                                            <p className="small">Add content through the Django admin interface</p>
                                            <a href="https://tours.dzsoftech.net/admin/" className="btn btn-sm btn-outline-primary" target="_blank" rel="noopener noreferrer">
                                                Open Admin
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4 text-center mb-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body">
                                            <div className="display-4 text-success mb-3">🔗</div>
                                            <h5>REST API</h5>
                                            <p className="small">Data flows through REST API endpoints</p>
                                            <a href="https://dzsoftech.net/tours/api/destinations/" className="btn btn-sm btn-outline-success" target="_blank" rel="noopener noreferrer">
                                                View API
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4 text-center mb-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body">
                                            <div className="display-4 text-info mb-3">⚛️</div>
                                            <h5>React Frontend</h5>
                                            <p className="small">Content displays automatically in React components</p>
                                            <button className="btn btn-sm btn-outline-info" disabled>
                                                You're Here!
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="text-center mt-4">
                                <h5 className="mb-3">📚 Available API Endpoints</h5>
                                <div className="row text-start">
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li>🏞️ <code>/api/destinations/</code></li>
                                            <li>🎯 <code>/api/tours/</code></li>
                                            <li>🏃 <code>/api/activities/</code></li>
                                            <li>🏨 <code>/api/resorts/</code></li>
                                            <li>👨‍🏫 <code>/api/tour-guides/</code></li>
                                        </ul>
                                    </div>
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li>🛎️ <code>/api/services/</code></li>
                                            <li>🛍️ <code>/api/shop/</code></li>
                                            <li>📝 <code>/api/blog/</code></li>
                                            <li>📸 <code>/api/gallery/</code></li>
                                            <li>💰 <code>/api/pricing/</code></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default DemoHomepage;
